import { Calendar, Flame, TrendingUp, ChevronUp, ChevronDown, Target, Activity, BarChart, Dumbbell, Brain, Sparkles, AlertCircle, Lightbulb } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useExercises } from '@/context/ExerciseContext';
import { statsApi, analysisApi } from '@/services/api';
import { useState, useEffect } from 'react';
import type { WorkoutStats } from '@/types';
import { AnimatedCounter } from './animated-counter';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

export default function ProgressDashboard() {
  const { getExerciseProgress } = useExercises();
  const [stats, setStats] = useState<WorkoutStats | null>(null);
  const [gapAnalysis, setGapAnalysis] = useState<any>(null);
  const [loadingAnalysis, setLoadingAnalysis] = useState(false);
  const [analysisError, setAnalysisError] = useState<string | null>(null);

  const exerciseProgress = getExerciseProgress();

  useEffect(() => {
    const fetchStats = async () => {
      const result = await statsApi.getStats();
      if (result.success) {
        setStats(result.data || null);
      }
    };
    fetchStats();
  }, []);

  const fetchGapAnalysis = async () => {
    setLoadingAnalysis(true);
    setAnalysisError(null);
    try {
      const result = await analysisApi.getGapAnalysis();
      if (result.success && result.data) {
        setGapAnalysis(result.data);
      } else {
        setAnalysisError(result.error || 'Failed to analyze training gaps');
      }
    } catch (error) {
      setAnalysisError('Failed to analyze training gaps');
    } finally {
      setLoadingAnalysis(false);
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'text-red-500 bg-red-100 dark:bg-red-900/20';
      case 'medium':
        return 'text-orange-500 bg-orange-100 dark:bg-orange-900/20';
      case 'low':
        return 'text-blue-500 bg-blue-100 dark:bg-blue-900/20';
      default:
        return 'text-muted-foreground bg-muted';
    }
  };

  const getPriorityIcon = (priority: string) => {
    switch (priority) {
      case 'high':
        return <AlertCircle className="w-4 h-4" />;
      case 'medium':
        return <Lightbulb className="w-4 h-4" />;
      case 'low':
        return <Sparkles className="w-4 h-4" />;
      default:
        return <Brain className="w-4 h-4" />;
    }
  };

  return (
    <div className="space-y-4 md:space-y-6 w-full">
      {/* Enhanced Stats Cards with Animation */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 md:gap-6">
        {/* Streak Card - Enhanced with weekly progress */}
        <motion.div
          whileHover={{ y: -2 }}
          transition={{ duration: 0.2 }}
          className="bg-card border border-border rounded-lg p-4 md:p-6 md:col-span-2"
        >
          <div className="space-y-4">
            {/* Main streak display */}
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 dark:bg-orange-900/20 rounded-lg">
                <Flame className="w-5 h-5 text-orange-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Current Streak</p>
                <div className="flex items-baseline gap-2">
                  <span className="text-3xl md:text-4xl font-bold tracking-tight">
                    <AnimatedCounter value={stats?.gymStreak || 0} />
                  </span>
                  <span className="text-sm text-muted-foreground font-medium">weeks</span>
                </div>
              </div>
            </div>
            
            {/* Enhanced weekly progress with explanation */}
            {stats && (
              <motion.div 
                className="space-y-3"
                initial={{ opacity: 0, y: 5 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.1 }}
              >
                {/* Current week progress */}
                <div className="bg-muted/50 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-muted-foreground">This Week Progress</span>
                    <span className="text-sm font-bold text-foreground">
                      {stats.currentWeekVisits}/{stats.currentWeekTarget}
                    </span>
                  </div>
                  
                  {/* Progress dots with labels */}
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-1.5">
                      {Array.from({ length: stats.currentWeekTarget }).map((_, index) => (
                        <motion.div
                          key={index}
                          className={cn(
                            "w-2.5 h-2.5 rounded-full transition-colors duration-200",
                            index < stats.currentWeekVisits 
                              ? "bg-green-500" 
                              : "bg-muted-foreground/30"
                          )}
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          transition={{ duration: 0.2, delay: index * 0.05 }}
                        />
                      ))}
                    </div>
                    
                    {stats.daysLeftInWeek > 0 ? (
                      <span className="text-xs text-muted-foreground">
                        {stats.daysLeftInWeek} days left
                      </span>
                    ) : (
                      <span className="text-xs text-green-500 font-medium">
                        Week complete
                      </span>
                    )}
                  </div>
                  
                  {/* Explanation text */}
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    {stats.currentWeekVisits >= stats.currentWeekTarget 
                      ? "✓ Week goal achieved! This will count toward your streak."
                      : `Visit ${stats.currentWeekTarget - stats.currentWeekVisits} more times to maintain your streak.`
                    }
                  </p>
                </div>
              </motion.div>
            )}
          </div>
        </motion.div>

        {/* Monthly Stats */}
        <motion.div
          whileHover={{ y: -2 }}
          transition={{ duration: 0.2 }}
          className="bg-card border border-border rounded-lg p-4 md:p-6"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 dark:bg-blue-900/20 rounded-lg">
              <Calendar className="w-5 h-5 text-blue-500" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">This Month</p>
              <div className="text-2xl font-bold">
                <AnimatedCounter value={stats?.thisMonthGymVisits || 0} />
              </div>
              <p className="text-xs text-muted-foreground">visits</p>
            </div>
          </div>
        </motion.div>

        {/* Total Sessions */}
        <motion.div
          whileHover={{ y: -2 }}
          transition={{ duration: 0.2 }}
          className="bg-card border border-border rounded-lg p-4 md:p-6"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 dark:bg-green-900/20 rounded-lg">
              <Dumbbell className="w-5 h-5 text-green-500" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Total Sessions</p>
              <div className="text-2xl font-bold">
                <AnimatedCounter value={stats?.totalWorkouts || 0} />
              </div>
              <p className="text-xs text-muted-foreground">all time</p>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Exercise Progress */}
      <motion.div
        className="bg-card border border-border rounded-lg p-4 md:p-6"
        whileHover={{ y: -2 }}
        transition={{ duration: 0.2 }}
      >
        <div className="flex items-center gap-2 mb-4 md:mb-6">
          <Target className="w-5 h-5 text-blue-500" />
          <h2 className="text-lg md:text-xl font-semibold">Exercise Progress</h2>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 md:gap-4 lg:gap-6">
          {Object.entries(exerciseProgress).slice(0, 6).map(([exercise, data], index) => {
            const latest = data[data.length - 1];
            const previous = data[data.length - 2];
            const improvement = previous ? ((latest.weight - previous.weight) / previous.weight * 100).toFixed(1) : 0;
            
            return (
              <motion.div
                key={exercise}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
                className="bg-card border border-border rounded-lg p-4 md:p-6"
                whileHover={{ y: -2 }}
              >
                <div className="flex justify-between items-start mb-3 md:mb-4">
                  <h4 className="font-semibold text-sm md:text-base pr-2 flex-1 min-w-0">{exercise}</h4>
                  <div className="text-right flex-shrink-0">
                    <div className="text-2xl md:text-4xl font-bold tracking-tight">{latest.weight}kg</div>
                  </div>
                </div>
                
                {/* Progress indicator with trend */}
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    {parseFloat(improvement.toString()) > 0 ? (
                      <>
                        <ChevronUp className="w-4 h-4 text-green-500" />
                        <span className="text-sm font-medium text-green-500">+{improvement}% improvement</span>
                      </>
                    ) : parseFloat(improvement.toString()) < 0 ? (
                      <>
                        <ChevronDown className="w-4 h-4 text-red-500" />
                        <span className="text-sm font-medium text-red-500">{improvement}% decline</span>
                      </>
                    ) : (
                      <span className="text-sm text-muted-foreground">No change</span>
                    )}
                  </div>
                  <span className="text-sm font-medium text-muted-foreground">{data.length} sessions</span>
                </div>
                
                <div className="text-sm text-muted-foreground">
                  Last workout: {new Date(latest.timestamp).toLocaleDateString()}
                </div>
              </motion.div>
            );
          })}
        </div>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
        {/* Volume Trends */}
        <motion.div
          className="bg-card border border-border rounded-lg p-4 md:p-6"
          whileHover={{ y: -2 }}
          transition={{ duration: 0.2 }}
        >
          <div className="flex items-center gap-2 mb-4 md:mb-6">
            <BarChart className="w-5 h-5 text-purple-500" />
            <h2 className="text-lg md:text-xl font-semibold">Training Volume</h2>
          </div>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-muted/50 border border-border rounded-lg p-4">
                <div className="text-2xl font-bold tracking-tight">{stats?.thisMonthWorkouts || 0}</div>
                <p className="text-sm text-muted-foreground">Workouts this month</p>
              </div>
              <div className="bg-muted/50 border border-border rounded-lg p-4">
                <div className="text-2xl font-bold tracking-tight">
                  {Object.values(exerciseProgress).reduce((total, sessions) => 
                    total + sessions.reduce((sessionTotal, session) => sessionTotal + session.sets, 0), 0
                  )}
                </div>
                <p className="text-sm text-muted-foreground">Total sets logged</p>
              </div>
            </div>
            
            <div className="text-center py-4 text-muted-foreground">
              <Activity className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p className="text-sm">Consistency builds muscle, not just heavy weight</p>
            </div>
          </div>
        </motion.div>

        {/* Progressive Overload Tracker */}
        <motion.div
          className="bg-card border border-border rounded-lg p-4 md:p-6"
          whileHover={{ y: -2 }}
          transition={{ duration: 0.2 }}
        >
          <div className="flex items-center gap-2 mb-4 md:mb-6">
            <TrendingUp className="w-5 h-5 text-green-500" />
            <h2 className="text-lg md:text-xl font-semibold">Progressive Overload</h2>
          </div>
          
          <div className="space-y-4">
            {Object.entries(exerciseProgress).slice(0, 4).map(([exercise, data]) => {
              const latest = data[data.length - 1];
              const previous = data[data.length - 2];
              const totalVolume = latest.weight * latest.sets * latest.reps;
              const prevVolume = previous ? previous.weight * previous.sets * previous.reps : 0;
              const volumeImprovement = prevVolume ? ((totalVolume - prevVolume) / prevVolume * 100) : 0;
              
              return (
                <div key={exercise} className="bg-muted/50 border border-border rounded-lg p-3">
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="font-medium text-sm truncate">{exercise}</h4>
                    <div className="text-right">
                      <div className="text-lg font-bold">{latest.weight}kg</div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">Volume: {totalVolume.toLocaleString()}</span>
                    {volumeImprovement > 0 && (
                      <span className="flex items-center gap-1 text-green-500 font-medium">
                        <ChevronUp className="w-3 h-3" />
                        +{volumeImprovement.toFixed(1)}%
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </motion.div>
      </div>

      {/* AI Gap Analysis Section */}
      <motion.div
        className="bg-gradient-to-br from-purple-50 to-blue-50 dark:from-purple-950/20 dark:to-blue-950/20 border border-purple-200 dark:border-purple-800 rounded-lg p-4 md:p-6"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Brain className="w-5 h-5 text-purple-500" />
            <h2 className="text-lg md:text-xl font-semibold">AI Training Analysis</h2>
          </div>
          <Button
            onClick={fetchGapAnalysis}
            disabled={loadingAnalysis}
            size="sm"
            className="bg-purple-500 hover:bg-purple-600 text-white"
          >
            {loadingAnalysis ? (
              <>
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                  className="w-3 h-3 border-2 border-white border-t-transparent rounded-full mr-2"
                />
                Analyzing...
              </>
            ) : (
              <>
                <Sparkles className="w-3 h-3 mr-2" />
                Analyze Gaps
              </>
            )}
          </Button>
        </div>

        <AnimatePresence mode="wait">
          {analysisError && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="bg-red-100 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-4"
            >
              <p className="text-sm text-red-600 dark:text-red-400">{analysisError}</p>
            </motion.div>
          )}

          {gapAnalysis && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
              className="space-y-4"
            >
              {/* Summary */}
              <div className="bg-white/50 dark:bg-black/20 rounded-lg p-4 border border-purple-100 dark:border-purple-900">
                <p className="text-sm text-muted-foreground italic">{gapAnalysis.summary}</p>
                <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                  <span>{gapAnalysis.data_summary.workouts_analyzed} workouts analyzed</span>
                  <span>•</span>
                  <span>{gapAnalysis.data_summary.days_covered} days</span>
                  <span>•</span>
                  <span>{new Date(gapAnalysis.analyzed_at).toLocaleDateString()}</span>
                  {gapAnalysis.cached && <Badge variant="outline" className="text-xs">Cached</Badge>}
                </div>
              </div>

              {/* Insights */}
              <div className="space-y-3">
                {gapAnalysis.insights.map((insight: any, index: number) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                    className="bg-white dark:bg-card border border-border rounded-lg p-4"
                  >
                    <div className="flex items-start gap-3">
                      <div className={cn("p-2 rounded-lg", getPriorityColor(insight.priority))}>
                        {getPriorityIcon(insight.priority)}
                      </div>
                      <div className="flex-1 space-y-2">
                        <div className="flex items-start justify-between gap-2">
                          <h3 className="font-semibold text-sm">{insight.title}</h3>
                          <Badge variant="outline" className={cn("text-xs capitalize", getPriorityColor(insight.priority))}>
                            {insight.priority}
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground">{insight.description}</p>
                        <div className="bg-muted/50 rounded-lg p-3 border-l-2 border-purple-500">
                          <p className="text-xs font-medium text-foreground">
                            <span className="text-purple-500">💡 Recommendation:</span> {insight.recommendation}
                          </p>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}

          {!gapAnalysis && !analysisError && !loadingAnalysis && (
            <div className="text-center py-8">
              <Brain className="w-12 h-12 mx-auto mb-3 text-purple-400 opacity-50" />
              <p className="text-sm text-muted-foreground">Click "Analyze Gaps" to get AI-powered insights about your training</p>
            </div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
}